import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_25355c40-a335-460f-bd4b-faf08ba5507b/artifacts/71a1dr43_logo%20without%20background.png";
const LOGIN_IMAGE = "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, msLogin, msConfig } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle MS SSO callback
  useEffect(() => {
    const code = searchParams.get('code');
    if (code && msConfig) {
      handleMsCallback(code);
    }
  }, [searchParams, msConfig]);

  const handleMsCallback = async (code) => {
    setMsLoading(true);
    setError('');
    
    // Clear the code from URL immediately to prevent reuse on refresh
    const cleanUrl = `${window.location.origin}${window.location.pathname}`;
    window.history.replaceState({}, document.title, cleanUrl);
    
    try {
      const redirectUri = `${window.location.origin}/login`;
      await msLogin(code, redirectUri);
      toast.success('Welcome!');
      navigate('/');
    } catch (err) {
      // Handle specific OAuth errors
      const errorDetail = err.response?.data?.detail || 'Microsoft login failed';
      
      if (errorDetail.includes('AADSTS54005') || errorDetail.includes('already redeemed')) {
        setError('Login session expired. Please try again.');
      } else if (errorDetail.includes('AADSTS') || errorDetail.includes('authentication server')) {
        setError('Microsoft authentication failed. Please use email/password login or contact support.');
      } else {
        setError(errorDetail);
      }
      
      setMsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleMsLogin = async () => {
    console.log('🔵 SSO Login clicked');
    console.log('🔵 Current msConfig:', msConfig);
    
    setMsLoading(true);
    setError('');
    
    try {
      let config = msConfig;
      
      // If config not loaded, try to fetch it
      if (!config) {
        console.log('⚠️  Config not loaded, fetching now...');
        
        try {
          const backendUrl = window.location.origin;
          console.log('🔵 Fetching from:', `${backendUrl}/api/auth/ms-config`);
          
          const res = await fetch(`${backendUrl}/api/auth/ms-config`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          console.log('🔵 Response status:', res.status);
          
          if (!res.ok) {
            const errorText = await res.text();
            console.error('❌ API returned error:', res.status, errorText);
            throw new Error(`API returned ${res.status}`);
          }
          
          const data = await res.json();
          console.log('🔵 Config received:', data);
          
          if (!data || !data.enabled || !data.client_id || !data.authority) {
            console.error('❌ Invalid config:', data);
            throw new Error('Invalid or disabled MS config');
          }
          
          config = data;
        } catch (fetchError) {
          console.error('❌ Failed to fetch MS config:', fetchError);
          
          // More specific error message
          if (fetchError.message?.includes('Failed to fetch') || fetchError.message?.includes('NetworkError')) {
            setError('Cannot reach authentication server. Please check your connection or use email/password login below.');
          } else if (fetchError.message?.includes('Invalid or disabled')) {
            setError('Microsoft login is not configured. Please use email/password login below.');
          } else {
            setError('Authentication server error. Please use email/password login below.');
          }
          
          setMsLoading(false);
          return;
        }
      }
      
      // Build authorization URL
      console.log('✅ Building auth URL with config:', {
        client_id: config.client_id,
        authority: config.authority,
        redirect: window.location.origin
      });
      
      const params = new URLSearchParams({
        client_id: config.client_id,
        response_type: 'code',
        redirect_uri: `${window.location.origin}/login`,
        response_mode: 'query',
        scope: 'openid profile email User.Read',
        state: 'vibe6_login',
        prompt: 'select_account'
      });
      
      const authUrl = `${config.authority}/oauth2/v2.0/authorize?${params.toString()}`;
      console.log('✅ Redirecting to:', authUrl);
      
      // Redirect to Microsoft
      window.location.href = authUrl;
      
    } catch (err) {
      console.error('❌ SSO Error:', err);
      setError(`Failed to connect to Microsoft: ${err.message}. Please try again or use email/password login.`);
      setMsLoading(false);
    }
  };

  if (msLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-cyan-50/50 to-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Signing in with Microsoft...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src={LOGIN_IMAGE}
          alt="Modern office workspace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0CBCD6]/80 to-[#0CBCD6]/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <img 
            src={LOGO_URL} 
            alt="Vibe6 Logo" 
            className="w-48 mb-6"
          />
          <p className="text-lg opacity-90 max-w-md">
            Your complete intranet solution for employee management, payroll, CRM, and project tracking.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-b from-cyan-50/50 to-white">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="space-y-1 text-center">
            <img 
              src={LOGO_URL} 
              alt="Vibe6 Logo" 
              className="h-16 mx-auto mb-4 lg:hidden"
            />
            <CardTitle className="text-2xl font-bold font-['Manrope']">Welcome back</CardTitle>
            <CardDescription>Sign in to your Vibe6 Intranet account</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Microsoft SSO Button - Always enabled with fallback */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 mb-4"
              onClick={handleMsLogin}
              disabled={loading || msLoading}
              data-testid="ms-login-btn"
            >
              {msLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connecting to Microsoft...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none">
                    <rect width="10" height="10" fill="#F25022"/>
                    <rect x="11" width="10" height="10" fill="#7FBA00"/>
                    <rect y="11" width="10" height="10" fill="#00A4EF"/>
                    <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
                  </svg>
                  Sign in with Microsoft 365
                </>
              )}
            </Button>
            
            <div className="relative my-4">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
                or continue with email
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="animate-fadeIn">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@vibe6.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
